import { useEffect, useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, MenuItem, Typography } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarColores = () => {
  const navigate = useNavigate()
  const { state } = useLocation() // Recibimos la fila seleccionada desde la grilla
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Rescatamos el código original (clave primaria) por si el usuario lo modifica
  const [colcodigoViejo, setColcodigoViejo] = useState("")

  // Estado del formulario
  const [formData, setFormData] = useState({
    colcodigo: "",
    coldescri: "",
    colstatus: "A",
  })

  useEffect(() => {
    // Precargar los inputs con los valores de la fila enviada desde la grilla
    if (state) {
      setFormData({
        colcodigo: state.colcodigo || "",
        coldescri: state.coldescri || "",
        colstatus: state.colstatus || "A",
      })
      setColcodigoViejo(state.colcodigo || "")
    } else {
      navigate(-1) // Retorno de seguridad si entran directo a la URL sin data
    }
  }, [state, navigate])

  const {
    mutateAsync: SaveEdicionColor,
    isPending: isSavingEdicion,
    isError,
  } = useMutation({
    queryKey: ["isEditingColor"],
    fn: async (data) => {
      const response = await api.post("/Colores/updateColores", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  // Manejar cambios en los campos del formulario con limpieza y topes
  const handleInputChange = (field, value) => {
    let finalValue = typeof value === "string" ? value.toUpperCase() : value

    if (field === "colcodigo") finalValue = finalValue.slice(0, 3)
    else if (field === "coldescri") finalValue = finalValue.slice(0, 24)

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // Preparar payload tal cual lo espera el backend para la edición de clave primaria
    const payload = {
      colcodigoOld: colcodigoViejo,
      colcodigoNew: formData.colcodigo,
      coldescri: formData.coldescri,
      colstatus: formData.colstatus,
    }

    try {
      if (!payload.colcodigoNew.trim()) return showWarning("El Código del Color es obligatorio")
      if (!payload.coldescri.trim()) return showWarning("La Descripción del Color es obligatoria")

      await SaveEdicionColor(payload)
    } catch (error) {
      console.error("Error al editar el Color:", error)
      if (error?.code === "VALIDATION_ERROR") {
        showWarning(error.message)
      }
    }
  }

  // Acción dinámica de la barra de menú basada en permisos
  const actualizarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action?.acccaption === "GRABAR")

  // Retorna arreglo vacío si el permiso no existe, evitando el botón default
  const toolbarActions = actualizarAction
    ? [
        {
          label: actualizarAction.acccaption,
          key: actualizarAction.acccaption,
          icon: getIconComponent(actualizarAction.accnameicono, actualizarAction.acctipoico),
        },
      ]
    : []

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* Toolbar superior con el botón de Guardar/Actualizar */}
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingEdicion}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mr: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Editar Color: {colcodigoViejo}</b>
        </div>

        <CustomBackdrop isLoading={isSavingEdicion} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos del Color
            </Typography>
            <Grid container spacing={3}>
              {/* CÓDIGO COLOR */}
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código Color *"
                  value={formData.colcodigo}
                  onChange={(e) => handleInputChange("colcodigo", e.target.value)}
                  error={isError && !formData.colcodigo}
                  inputProps={{ maxLength: 3 }}
                  disabled
                />
              </Grid>

              {/* DESCRIPCIÓN */}
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.coldescri}
                  onChange={(e) => handleInputChange("coldescri", e.target.value)}
                  error={isError && !formData.coldescri}
                  inputProps={{ maxLength: 24 }}
                />
              </Grid>

              {/* ESTADO */}
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Estado"
                  value={formData.colstatus}
                  onChange={(e) => handleInputChange("colstatus", e.target.value)}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EditarColores
